/**
 * ClassificationDiff — PlanRenderer for classification_propagation plans.
 *
 * Renders a two-column diff:
 *   Left  — current purpose groupings (what the docs are classified as now)
 *   Right — proposed purpose groupings (what they'll become after propagation)
 *
 * Checkboxes on each row let the user opt out of specific changes.
 *
 * Props (from AgentPlanDrawer):
 *   plan             — propagation plan returned by the backend
 *   selection        — Set<string> of doc_ids currently selected for apply
 *   onSelectionChange — callback(newSet) when user toggles a doc
 */
import { useMemo } from 'react';

function purposeLabel(raw) {
  if (!raw) return 'Unclassified';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function ClassificationDiff({ plan, selection, onSelectionChange }) {
  if (!plan || !plan.corrections?.length) {
    return (
      <div className="ap-empty">
        No propagation candidates found for the current corrections.
      </div>
    );
  }

  // Flatten all matches with their source context
  const allMatches = useMemo(() => {
    const flat = [];
    for (const corr of plan.corrections) {
      for (const m of corr.matches || []) {
        flat.push({ ...m, source_filename: corr.source_filename, source_purpose: corr.source_purpose });
      }
    }
    return flat;
  }, [plan]);

  // Group by proposed_purpose for the right column
  const byProposed = useMemo(() => {
    const groups = {};
    for (const m of allMatches) {
      const key = m.proposed_purpose || 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    }
    return groups;
  }, [allMatches]);

  // Group by current_purpose for the left column
  const byCurrent = useMemo(() => {
    const groups = {};
    for (const m of allMatches) {
      const key = m.current_purpose || 'unclassified';
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    }
    return groups;
  }, [allMatches]);

  function toggleDoc(docId) {
    const next = new Set(selection);
    if (next.has(docId)) next.delete(docId);
    else next.add(docId);
    onSelectionChange(next);
  }

  function toggleGroup(docIds) {
    const next = new Set(selection);
    const allOn = docIds.every(id => next.has(id));
    if (allOn) docIds.forEach(id => next.delete(id));
    else docIds.forEach(id => next.add(id));
    onSelectionChange(next);
  }

  const total = allMatches.length;
  const selectedCount = allMatches.filter(m => selection.has(m.doc_id)).length;

  return (
    <div className="ap-diff">
      <div className="ap-diff-summary">
        <strong>{total}</strong> document{total !== 1 ? 's' : ''} proposed for reclassification.
        {' '}
        <span className="ap-diff-selected">{selectedCount} selected.</span>
        {' '}
        <button
          className="ap-diff-toggle-all"
          onClick={() => {
            if (selectedCount === total) onSelectionChange(new Set());
            else onSelectionChange(new Set(allMatches.map(m => m.doc_id)));
          }}
        >
          {selectedCount === total ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      <div className="ap-diff-cols">
        {/* Left: current */}
        <div className="ap-diff-col">
          <div className="ap-diff-col-header">Current classification</div>
          {Object.entries(byCurrent).map(([purpose, matches]) => {
            const groupIds = matches.map(m => m.doc_id);
            const allSelected = groupIds.every(id => selection.has(id));
            return (
              <div key={purpose} className="ap-diff-group ap-diff-group--current">
                <div
                  className="ap-diff-group-header"
                  onClick={() => toggleGroup(groupIds)}
                  title="Click to toggle all in group"
                >
                  <span className="ap-diff-group-label">{purposeLabel(purpose)}</span>
                  <span className="ap-diff-group-count">{matches.length}</span>
                  <span className={`ap-diff-group-check ${allSelected ? 'ap-diff-group-check--on' : ''}`}>
                    {allSelected ? '☑' : '☐'}
                  </span>
                </div>
                {matches.map(m => (
                  <DocRow
                    key={m.doc_id}
                    match={m}
                    selected={selection.has(m.doc_id)}
                    onToggle={() => toggleDoc(m.doc_id)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div className="ap-diff-divider">→</div>

        {/* Right: proposed */}
        <div className="ap-diff-col">
          <div className="ap-diff-col-header">Proposed classification</div>
          {Object.entries(byProposed).map(([purpose, matches]) => (
            <div key={purpose} className="ap-diff-group ap-diff-group--proposed">
              <div className="ap-diff-group-header">
                <span className="ap-diff-group-label ap-diff-group-label--new">
                  {purposeLabel(purpose)}
                </span>
                <span className="ap-diff-group-count">{matches.length}</span>
              </div>
              {matches.map(m => (
                <div
                  key={m.doc_id}
                  className={`ap-diff-doc ap-diff-doc--proposed ${selection.has(m.doc_id) ? 'ap-diff-doc--selected' : 'ap-diff-doc--skipped'}`}
                  title={`Confidence: ${(m.confidence * 100).toFixed(0)}% — ${m.match_reason}`}
                >
                  <span className="ap-diff-doc-name">{m.filename}</span>
                  {m.confidence < 1 && (
                    <span className="ap-diff-doc-conf">{(m.confidence * 100).toFixed(0)}%</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Per-correction source summary */}
      <details className="ap-diff-sources">
        <summary>Based on {plan.corrections_count} human correction{plan.corrections_count !== 1 ? 's' : ''}</summary>
        <ul className="ap-diff-sources-list">
          {plan.corrections.map(c => (
            <li key={c.source_doc_id}>
              <strong>{c.source_filename}</strong> → {purposeLabel(c.source_purpose)}
              {' '}({c.matches?.length || 0} candidate{c.matches?.length !== 1 ? 's' : ''})
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function DocRow({ match, selected, onToggle }) {
  return (
    <label className={`ap-diff-doc ap-diff-doc--current ${selected ? 'ap-diff-doc--selected' : 'ap-diff-doc--skipped'}`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="ap-diff-doc-cb"
      />
      <span className="ap-diff-doc-name">{match.filename}</span>
      {match.confidence < 1 && (
        <span className="ap-diff-doc-conf" title={match.match_reason}>
          {(match.confidence * 100).toFixed(0)}%
        </span>
      )}
    </label>
  );
}
